// CursoContenidoSection.js — Sección `curso_contenido`: la página de un curso
// (docs/Lac/curso.png). Tarjeta con el avance («0% COMPLETADO 0/8 pasos») y el
// «Contenido del Curso»: cada lección con su círculo de estado, que lleva a su
// vista: por defecto el modo mentor de la plataforma en esa lección (con todas sus
// funciones y «volver» a esta página); con `vista: 'aula'`, el aula tipo LearnDash.
//
// Config: documento_id, titulo, texto_sin_sesion, fondo, padding, ancho_maximo,
// color_primario, color_secundario. El avance es el mismo del modo mentor
// (mentor/start.php), así cuenta para certificados y analítica.
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { mentor2Service } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { leccionesDelCurso, avanceDelCurso, urlLeccion, urlLeccionMentor } from '../../aula/aulaCurso';

const Seccion = styled.section`
  background: ${p => p.$fondo};
  padding: ${p => p.$padding};
  font-family: Arial, Helvetica, sans-serif;
  @media (max-width: 767px) { padding: 36px 0 44px; }
`;

const Tarjeta = styled.div`
  width: 90%;
  max-width: ${p => p.$ancho};
  margin: 0 auto;
  box-sizing: border-box;
  background: #fff;
  border-radius: 23px;
  padding: 42px 42px 60px;
  @media (max-width: 767px) { padding: 24px 18px 30px; }
`;

export const BarraAvance = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  background: #eef1f4;
  border-radius: 6px;
  padding: 13px 18px;
  .barra { flex: 1; height: 6px; background: #fff; border-radius: 6px; overflow: hidden; }
  .barra span { display: block; height: 100%; background: ${p => p.$primario}; transition: width .4s ease; }
  .texto { white-space: nowrap; font-size: 13px; color: #6b7280; }
  .texto strong { font-family: 'Roboto', Arial, sans-serif; font-weight: 800; letter-spacing: .3px; color: ${p => p.$secundario}; margin-right: 6px; }
  @media (max-width: 600px) { flex-direction: column-reverse; align-items: stretch; gap: 8px; }
`;

const Titulo = styled.h2`
  margin: 40px 0 26px;
  padding: 0;
  font-family: 'Roboto', Helvetica, Arial, sans-serif;
  font-size: 26px;
  font-weight: 700;
  color: ${p => p.$color};
`;

const Lista = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const Item = styled(Link)`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 20px;
  padding: 22px 24px;
  border: 2px solid #e2e7ed;
  border-radius: 6px;
  color: #444;
  font-size: 20px;
  line-height: 1.3;
  text-decoration: none;
  transition: border-color .2s ease, box-shadow .2s ease;
  &:hover, &:focus-visible { border-color: ${p => p.$primario}; box-shadow: 0 4px 14px rgb(0 0 0 / .05); outline: none; }
  @media (max-width: 767px) { font-size: 16px; padding: 16px; }
`;

export const Circulo = styled.span`
  flex: 0 0 auto;
  width: ${p => p.$size || 26}px;
  height: ${p => p.$size || 26}px;
  border-radius: 50%;
  box-sizing: border-box;
  border: 3px solid ${p => (p.$hecho ? p.$color : (p.$actual ? p.$color : '#e2e7ed'))};
  background: ${p => (p.$hecho ? p.$color : 'transparent')};
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  svg { width: 60%; height: 60%; }
`;

export const Check = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);

const Aviso = styled.p`
  margin: 30px 0 0;
  color: #6b7280;
  font-size: 16px;
  button { margin-left: 8px; border: none; background: none; color: ${p => p.$primario}; font-weight: 700; cursor: pointer; font-size: 16px; padding: 0; }
`;

const CursoContenidoSection = ({ config = {}, proyecto, openLogin, tl = (v, d = '') => v || d }) => {
  const c = config;
  const { user } = useAuth() || {};
  const docId = Number(c.documento_id) || 0;
  const primario = c.color_primario || '#8800cc';
  const secundario = c.color_secundario || '#60068d';
  const location = useLocation();
  // Con el aula del proyecto encendida, las lecciones se abren en ella; si no, en el modo mentor.
  const enAula = c.vista === 'aula' || !!proyecto?.config_json?.aula?.activo;
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user || !docId) return;
    let cancelado = false;
    mentor2Service.start(docId)
      .then(r => { if (!cancelado) setDatos(r.data?.data || null); })
      .catch(err => { if (!cancelado) setError(err.response?.data?.message || 'No se pudo cargar el curso.'); });
    return () => { cancelado = true; };
  }, [user, docId]);

  const lecciones = leccionesDelCurso(datos);
  const avance = avanceDelCurso(lecciones);

  return (
    <Seccion $fondo={c.fondo || '#f8f5ff'} $padding={c.padding || '60px 0'}>
      <Tarjeta $ancho={c.ancho_maximo || '895px'}>
        <BarraAvance $primario={primario} $secundario={secundario}>
          <div className="barra" aria-hidden="true"><span style={{ width: `${avance.porcentaje}%` }} /></div>
          <div className="texto"><strong>{avance.porcentaje}% COMPLETADO</strong>{avance.hechas}/{avance.total} pasos</div>
        </BarraAvance>
        <Titulo $color={c.color_titulo || '#0f3a6b'}>{tl(c.titulo, 'Contenido del Curso')}</Titulo>

        {!user && (
          <Aviso $primario={primario}>
            {tl(c.texto_sin_sesion, 'Inicia sesión para ver las lecciones y tu avance.')}
            {openLogin && <button type="button" onClick={() => openLogin({ destino: window.location.pathname })}>Iniciar sesión</button>}
          </Aviso>
        )}
        {error && <Aviso $primario={primario}>{error}</Aviso>}
        {user && !datos && !error && <Aviso $primario={primario}>Cargando el contenido…</Aviso>}

        {lecciones.length > 0 && (
          <Lista>
            {lecciones.map(l => (
              <li key={`${l.modulo}-${l.numero}`}>
                <Item
                  to={enAula ? urlLeccion(docId, l, proyecto) : urlLeccionMentor(docId, l)}
                  state={enAula ? undefined : { volverA: location.pathname }}
                  $primario={primario}
                  aria-label={`${l.titulo}${l.completado ? ' (completada)' : ''}`}
                >
                  <Circulo $hecho={l.completado} $color={secundario} aria-hidden="true">{l.completado && <Check />}</Circulo>
                  <span>{l.titulo}</span>
                </Item>
              </li>
            ))}
          </Lista>
        )}
      </Tarjeta>
    </Seccion>
  );
};

export default CursoContenidoSection;
